/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useRef} from 'react';
import {FixedSizeGrid as Grid} from 'react-window';
import {makeStyles} from '@material-ui/core/styles';
import {GenericObject} from '@ion/core/src/types/entities';
import {CheckboxState} from '@ion/core/src/types/general';
import CheckBox from '@ion/dls/src/components/checkbox/CheckBox';
import {IconCheckboxLabel} from '@ion/dls/src/components/checkbox/IconCheckboxLabel';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {useWindowSize} from '@ion/dls/src/hooks/useWindowSize';
import {DLSTheme} from '@ion/dls/src/types/types';
import {FilterHashMapKeyType} from './FilterHelpers';
import {FilterSearchResults} from './FilterSearchResults';

export interface FilterContentGridProps {
  optionItems: string[];
  optionData: FilterHashMapKeyType;
  onOptionSelected: Function;
  onGroupSelected: Function;
  contentGridTemplateColumns: string;
  contentJustifyContent: string;
  contentGridColumnGap: string;
  contentGridRowGap: string;
}

export interface FilterContentListProps {
  searchValue: string;
  optionType: string;
  optionItems: string[];
  optionData: FilterHashMapKeyType;
  clearSearchValue: Function;
  onOptionSelected: Function;
  onGroupSelected: Function;
}

interface UseStylesProps {
  contentGridTemplateColumns: string;
  contentJustifyContent: string;
  contentGridColumnGap: string;
  contentGridRowGap: string;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  eventMenuSelector : {
    marginRight : '24px',
    minWidth    : '194px'
  },
  contentGridContainer : {
    maxHeight                      : '260px',
    overflowX                      : 'hidden',
    overflowY                      : 'auto',
    marginLeft                     : '10px',
    marginBottom                   : '20px',
    paddingLeft                    : '10px',
    [theme.breakpoints.down('sm')] : {
      maxHeight    : 'calc(100vh - 200px)',
      marginBottom : '0px'
    }
  },
  contentListContainer : {
    maxHeight                      : '260px',
    overflowX                      : 'hidden',
    overflowY                      : 'auto',
    marginLeft                     : '10px',
    marginBottom                   : '20px',
    paddingLeft                    : '10px',
    [theme.breakpoints.down('sm')] : {
      maxHeight    : '220px',
      marginTop    : '25px',
      marginBottom : '0px'
    }
  },
  contentGrid : {
    display             : 'grid',
    gridTemplateColumns : ({contentGridTemplateColumns}: UseStylesProps) => contentGridTemplateColumns,
    justifyContent      : ({contentJustifyContent}: UseStylesProps) => contentJustifyContent,
    gridColumnGap       : ({contentGridColumnGap}: UseStylesProps) => contentGridColumnGap,
    gridRowGap          : ({contentGridRowGap}: UseStylesProps) => contentGridRowGap
  }
}));

// Defines the default values for the grid
const gridConfig = {
  desktop : {
    columns     : 15,
    rowHeight   : 50,
    offsetWidth : 100,
    maxHeight   : 185,
    columnWidth : 240
  },
  mobile : {
    columns     : 1,
    rowHeight   : 50,
    offsetWidth : 0,
    maxHeight   : 250,
    columnWidth : 300
  }
};

/**
 * The FilterContentGrid component renders the content from the data passed in
 * through the optionItems and optionData props into a grid configured by the
 * contentGridTemplateColumns, contentJustifyContent, contentGridColumnGap,
 * and contentGridRowGap props. It is intended to be used within the main
 * Filter component.
 *
 * ```typescript
 *
 * import FilterContentGrid from './FilterContentGrid';
 *
 * <FilterContentGrid optionItems={filterContentOptions}
 *   optionData={filterHashMap}
 *   onOptionSelected={handleOptionSelected}
 *   onGroupSelected={handleGroupSelected}
 *   contentGridTemplateColumns={contentGridTemplateColumns}
 *   contentJustifyContent={contentJustifyContent}
 *   contentGridColumnGap={contentGridColumnGap}
 *   contentGridRowGap={contentGridRowGap} />
 *
 * ```
 */
export const FilterContentGrid = ({
  optionItems,
  optionData,
  onOptionSelected,
  onGroupSelected,
  contentGridTemplateColumns,
  contentJustifyContent,
  contentGridColumnGap,
  contentGridRowGap
}: FilterContentGridProps) => {
  const classes = useStyles({
    contentGridTemplateColumns,
    contentJustifyContent,
    contentGridColumnGap,
    contentGridRowGap
  });

  const {width}   = useWindowSize();
  const {isPhone} = useMedia();

  const {desktop, mobile} = gridConfig;
  const {
    columnWidth,
    columns,
    rowHeight,
    offsetWidth,
    maxHeight
  } = !isPhone ? desktop : mobile;
  const columnCount = useRef(columns);

  // Getting the width of the document or container
  const {documentElement, body} = document;
  const documentWidth = documentElement?.clientWidth || body?.clientWidth;
  const container = document.getElementById('menu-content');
  const containerWidth = container?.offsetWidth;

  // Setting the width of the grid
  const gridWidth = (containerWidth + 100) || width || documentWidth;

  /**
   * Defines getGridRows method to retrieve # of rows
   */
  const getGridRows = (optionItems: any[]): number => {
    return Math.ceil(optionItems?.length / columnCount?.current);
  };

  /**
   * Defines getColumnCount method to retrieve # of columns
   */
  const getColumnCount = (width: number): number => {
    const columnsCount  = Math.floor((width - offsetWidth) / columnWidth);
    columnCount.current = columnsCount <= columns ? columnsCount : columns;
    return columnCount.current;
  };

  /**
   * Defines getGridHeight method to retrieve height of grid
   */
  const getGridHeight = (optionItems: any[]): number => {
    const height = getGridRows(optionItems) * rowHeight + 25;
    return height <= maxHeight ? height : maxHeight;
  };

  // defines the getGridColumnWidth to retrieve column width of grid...
  const getGridColumnWidth = () => (gridWidth / columnCount?.current) - 20;

  // defines the getGridLabelWidth to retrieve width of grid label...
  const getGridLabelWidth = () => {
    if (!isPhone) {
      const columnWidth = getGridColumnWidth();
      return `${columnWidth - 40}px`;
    }
    return 'auto';
  };

  const renderCell = ({
    columnIndex,
    rowIndex,
    style
  }: {
    columnIndex: number,
    rowIndex: number,
    style: GenericObject
  }) => {
    const itemId = optionItems[rowIndex * columnCount.current + columnIndex];
    const item   = optionData[itemId];
    return (
      <>
        {item && (
          <div style={style}>
            <CheckBox key={`filterContentGrid-${itemId}`}
              labelProps
              labelwidth={getGridLabelWidth()}
              style={{marginLeft: '15px'}}
              checked={item.checked === CheckboxState.Checked}
              label={item.childrenIds.length
                ? <IconCheckboxLabel label={item.title}
                  onIconClick={function(e) {
                    e.stopPropagation();
                    e.preventDefault();
                    onGroupSelected(itemId);
                  }} />
                : item.title}
              onChange={function({target:{checked}}: ChangeEvent<HTMLInputElement>) {
                onOptionSelected(itemId, checked);
              }} />
          </div>
        )}
      </>
    );
  };

  return (
    <div>
      <Grid style={{width: '100%', overflowY: 'auto', overflowX: 'hidden'}}
        columnCount={getColumnCount(gridWidth)}
        columnWidth={getGridColumnWidth()}
        height={getGridHeight(optionItems)}
        rowCount={getGridRows(optionItems)}
        rowHeight={rowHeight}
        width={gridWidth}>
        {renderCell}
      </Grid>
    </div>
  );
};

/**
 * The FilterContentList component takes a specified search value and renders
 * the content from the data passed in through the optionItems and optionData
 * props into a search results list. It is intended to be used within the main
 * Filter component.
 *
 * ```typescript
 *
 * import FilterContentList from './FilterContentGrid';
 *
 * <FilterContentList searchValue={searchValue}
 *   optionType={optionType}
 *   optionItems={filterContentOptions}
 *   optionData={filterHashMap}
 *   clearSearchValue={clearSearchValue}
 *   onOptionSelected={handleOptionSelected}
 *   onGroupSelected={handleGroupSelected} />
 *
 * ```
 */
export const FilterContentList = ({
  searchValue,
  optionType,
  optionItems,
  optionData,
  clearSearchValue,
  onOptionSelected,
  onGroupSelected
}: FilterContentListProps) => {
  const classes = useStyles();

  return (
    <div className={classes.contentListContainer}>
      <FilterSearchResults searchValue={searchValue}
        optionType={optionType}
        optionItems={optionItems}
        optionData={optionData}
        clearSearchValue={clearSearchValue}
        onOptionSelected={onOptionSelected}
        onGroupSelected={onGroupSelected} />
    </div>
  );
};
